/**
 * Contrato PURO dos caps de anexo do Executor — sem React, sem Supabase.
 *
 * Separado de useExecutorCaps.ts de propósito: o hook importa o client do
 * Supabase, que exige env no import. O parser é a parte que precisa de teste
 * (é ele que decide se o compositor autoriza ou não um upload), então mora
 * onde o vitest alcança sem subir nada.
 */

export interface ExecutorCaps {
  filesPerDay: number;
  maxBytes: number;
  maxPdfPages: number;
  maxSheetCells: number;
}

export interface ExecutorCapsState {
  plan: string;
  caps: ExecutorCaps;
  formatsEnabled: { image: boolean; pdf: boolean; sheet: boolean };
  usedToday: number;
  /**
   * Anexos por turno (F1 PR 2). Vem pela MESMA RPC dos outros tetos para o
   * compositor usar exatamente o número que o servidor usa — antes era
   * hardcode duplicado nos dois lados, e mudar exigia deploy de duas edge
   * functions mais um build do front, que não sobem juntos.
   */
  maxPerTurn: number;
}

/**
 * Default conservador, usado enquanto o RPC não respondeu ou quando ele falha.
 * Espelha o seed `free` de `executor.attachment_caps`. Conservador de
 * propósito: um teto baixo demais gera um erro específico e acionável; um alto
 * demais deixa o usuário subir 20 MB para ouvir "não" do servidor depois.
 */
export const FALLBACK_CAPS: ExecutorCapsState = {
  plan: "free",
  caps: { filesPerDay: 3, maxBytes: 5 * 1024 * 1024, maxPdfPages: 20, maxSheetCells: 2000 },
  formatsEnabled: { image: true, pdf: true, sheet: true },
  usedToday: 0,
  maxPerTurn: 10,
};

/** Normaliza o jsonb do RPC — campo a campo, com fallback por campo. */
export function parseCapsPayload(raw: unknown): ExecutorCapsState {
  if (!raw || typeof raw !== "object") return FALLBACK_CAPS;
  const r = raw as Record<string, unknown>;
  const caps = (r.caps && typeof r.caps === "object" ? r.caps : {}) as Record<string, unknown>;
  const enabled = (r.formatsEnabled && typeof r.formatsEnabled === "object"
    ? r.formatsEnabled
    : {}) as Record<string, unknown>;

  const num = (v: unknown, fallback: number): number =>
    typeof v === "number" && isFinite(v) && v > 0 ? Math.floor(v) : fallback;
  const flag = (v: unknown): boolean => v !== false;

  return {
    plan: typeof r.plan === "string" ? r.plan : FALLBACK_CAPS.plan,
    caps: {
      filesPerDay: num(caps.filesPerDay, FALLBACK_CAPS.caps.filesPerDay),
      maxBytes: num(caps.maxBytes, FALLBACK_CAPS.caps.maxBytes),
      maxPdfPages: num(caps.maxPdfPages, FALLBACK_CAPS.caps.maxPdfPages),
      maxSheetCells: num(caps.maxSheetCells, FALLBACK_CAPS.caps.maxSheetCells),
    },
    formatsEnabled: {
      image: flag(enabled.image),
      pdf: flag(enabled.pdf),
      sheet: flag(enabled.sheet),
    },
    usedToday: typeof r.usedToday === "number" && r.usedToday >= 0 ? Math.floor(r.usedToday) : 0,
    // Clamp espelhando o da RPC (piso 1, teto 20): 0 travaria qualquer anexo,
    // e um valor absurdo viraria carga. Server velho (sem o campo) cai no
    // default — é a janela de deploy, e ela não pode quebrar o compositor.
    maxPerTurn: num(r.maxPerTurn, FALLBACK_CAPS.maxPerTurn) > 20
      ? 20
      : num(r.maxPerTurn, FALLBACK_CAPS.maxPerTurn),
  };
}
